import { STATUS_COLETADA } from '../../../meta'

/**
 * Subconjunto do estado do formulário necessário para validar. Tipado de forma
 * estrutural para a função continuar pura e testável fora do React.
 */
export interface CamposParaValidar {
  data_da_os?: string
  propriedade_id?: number | null
  talhao_id?: number | null
  laboratorio_id?: number | null
  safra_consultoria_id?: number | null
  responsaveis_selecionados?: unknown[]
  coleta_liberada?: string
  coletada_em?: string
  status_atual_id?: number | null
  representante_selecionado_id?: number | null
}

export interface ContextoValidacao {
  /** Modo editar: habilita as regras que só valem para OS já criada. */
  isEdicao: boolean
  /**
   * Modo criar. Só nele o talhão é obrigatório: em editar e clonar ele vem da
   * OS de origem e o campo fica desabilitado na tela.
   */
  ehCriar: boolean
  /** Cliente do tipo 'G' (grupo) exige representante legal. */
  ehGrupo: boolean
  /** A OS precisa de ao menos uma análise para o backend aceitar. */
  possuiAnalises: boolean
  /**
   * Pontos da malha que caem fora do contorno do talhão. A importação de
   * shapefile os aceita — é no salvar que a OS é barrada, porque coletar fora
   * do talhão não é uma OS válida.
   */
  pontosForaDoTalhao?: number[]
}

/** Campos que podem exibir erro, na ordem em que aparecem na tela. */
export type CampoComErro =
  | 'representante'
  | 'data_da_os'
  | 'propriedade'
  | 'talhao'
  | 'laboratorio'
  | 'safra_consultoria'
  | 'responsaveis'
  | 'coleta_liberada'
  | 'coletada_em'
  | 'analises'
  | 'pontos_fora'

/** Mensagem de erro por campo. Ausência de chave = campo válido. */
export type ErrosFormulario = Partial<Record<CampoComErro, string>>

/**
 * Valida os campos obrigatórios antes de salvar.
 *
 * Existe porque o `obrigatorio` dos componentes é apenas o asterisco visual: sem
 * esta checagem a OS é enviada incompleta e o erro só aparece como recusa do
 * backend. Espelha as validações da versão em produção
 * (`ordemLaboratorio/index.tsx:382-401` e `:716-720`), que lá estavam espalhadas
 * entre "adicionar análise" e "salvar".
 *
 * Retorna um mapa campo -> mensagem, consumido pelas props `error`/`errorMessage`
 * de cada componente. As chaves são inseridas na ordem visual dos campos, então
 * a primeira também serve para um aviso geral.
 */
export function validarFormularioOs(
  campos: CamposParaValidar,
  { isEdicao, ehCriar, ehGrupo, possuiAnalises, pontosForaDoTalhao }: ContextoValidacao
): ErrosFormulario {
  const erros: ErrosFormulario = {}

  if (ehGrupo && !campos.representante_selecionado_id) {
    erros.representante = 'Selecione um representante legal'
  }

  if (!campos.data_da_os) {
    erros.data_da_os = 'Informe a data da O.S'
  }

  // A propriedade é exigida em qualquer cenário — criar, clonar e editar.
  if (!campos.propriedade_id) {
    erros.propriedade = 'Selecione a propriedade'
  }

  // O talhão só é obrigatório ao CRIAR (regra revista em 26/08/2026). Em editar e
  // clonar ele vem da OS de origem, com o campo desabilitado na tela — exigi-lo
  // travava o salvamento quando o backend não devolvia o cod.
  if (ehCriar && !campos.talhao_id) {
    erros.talhao = 'Selecione o talhão'
  }

  if (!campos.laboratorio_id) {
    erros.laboratorio = 'Selecione o laboratório'
  }

  if (!campos.safra_consultoria_id) {
    erros.safra_consultoria = 'Selecione a safra consultoria'
  }

  if ((campos.responsaveis_selecionados?.length ?? 0) === 0) {
    erros.responsaveis = 'Selecione ao menos um responsável'
  }

  if (!campos.coleta_liberada) {
    erros.coleta_liberada = 'Informe a data de liberação'
  }

  // "Coletado em" só é editável (e exigível) quando a OS está em COLETADA.
  if (isEdicao && campos.status_atual_id === STATUS_COLETADA && !campos.coletada_em) {
    erros.coletada_em = 'Informe a data da coleta'
  }

  // A metade "Análises" não tem campo próprio para exibir erro — vira alerta.
  // Sem campo na tela: a mensagem sai no alerta do salvar, com os números dos
  // pontos para o usuário localizá-los no mapa.
  if ((pontosForaDoTalhao?.length ?? 0) > 0) {
    const nomes = (pontosForaDoTalhao ?? []).join(', ')
    erros.pontos_fora =
      (pontosForaDoTalhao ?? []).length === 1
        ? `O ponto ${nomes} está fora do talhão. Remova-o ou reposicione-o para salvar.`
        : `Os pontos ${nomes} estão fora do talhão. Remova-os ou reposicione-os para salvar.`
  }

  if (!possuiAnalises) {
    erros.analises = 'Adicione ao menos uma análise'
  }

  return erros
}

/** `true` quando não há nenhuma pendência. */
export function formularioValido(erros: ErrosFormulario): boolean {
  return Object.keys(erros).length === 0
}
